import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Re-reads the cart from Shopify and re-renders every cart surface from that
 * answer, whenever a page comes back from the back-forward cache or a tab that
 * had been left alone is returned to.
 *
 * Why this exists. The back-forward cache restores a document exactly as it
 * was: the DOM and the JavaScript heap, untouched. Every cart surface in this
 * theme, the drawer, the cart page and the header count, is only ever updated
 * in answer to a cart mutation made *in that document*. So a page put away
 * before later mutations, made on another page, in another tab, or by the
 * checkout itself, comes back showing the cart as it was when it left, and
 * nothing asks the server whether that is still true.
 *
 * That is the bug the owner hit: three items added, checkout opened, Back
 * pressed, and the page that came back was one whose drawer had been rendered
 * when the cart held a single item. The cart was never wrong; the picture of it
 * was. Removing that item appeared to fix it because a removal is a mutation,
 * and a mutation is the first thing that makes a restored document read the
 * real cart.
 *
 * The one thing this must not do is switch the back-forward cache off to dodge
 * the problem. It is worth real speed on a phone, and a page is perfectly
 * capable of correcting itself once it knows to look.
 *
 * Rendering goes through the theme's own section renderer, the same path a
 * removal uses, and the HTML that comes back is passed on in the cart update
 * event so the header count and everything else reads from that one answer.
 * There is no second, local idea of what is in the cart for the two to
 * disagree about.
 */

/** A tab flicked past for a moment is not a tab left alone. */
const AWAY_MS = 1000;

/** Two restores in the same instant are one restore. */
const COOLDOWN_MS = 600;

let inFlight = false;
let lastRun = 0;
let hiddenAt = 0;
let blurredAt = 0;

/** Every cart surface on this page: the drawer, the cart page, or both. */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (id) ids.add(id);
  }
  return [...ids];
}

/**
 * The cart total as the server just rendered it. An empty cart renders no such
 * element, which is why this can come back null and the caller asks the cart
 * itself instead.
 *
 * @param {Record<string, string>} sections
 * @returns {number | null}
 */
function countFromSections(sections) {
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
    if (!el) continue;
    const n = parseInt(el.textContent ?? '', 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** The cart's own count, for the empty case and for anything unexpected. */
async function countFromCart() {
  try {
    const response = await fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`, { cache: 'no-store' });
    if (!response.ok) return null;
    const cart = await response.json();
    return typeof cart.item_count === 'number' ? cart.item_count : null;
  } catch (_) {
    return null;
  }
}

/**
 * Ask Shopify for the cart surfaces as they stand and render from the answer.
 * Safe to call at any time, and safe to call twice.
 *
 * @param {string} reason - For the console when something goes wrong.
 */
export async function refreshCartFromServer(reason = 'unknown') {
  if (inFlight || Date.now() - lastRun < COOLDOWN_MS) return;

  const ids = cartSectionIds();
  if (ids.length === 0) return;

  inFlight = true;
  try {
    // `renderSection` fetches with `?section_id=` and morphs, which is the path
    // the rest of the theme already uses. Note the cache is turned off: the
    // whole point here is to get past a stale picture of the cart.
    //
    // It must NOT be asked for as JSON. A product URL answers an
    // `Accept: application/json` request with the product object, so the first
    // version of this quietly fetched a product and rendered nothing.
    // A unique parameter on every request. `cache: false` only bypasses the
    // renderer's own in-memory store; the browser's HTTP cache is a separate
    // thing, and it is the one that matters here. The page we are correcting
    // may itself have just been served from that cache on a Back, and the
    // section URL for it was fetched when the page first loaded, so without
    // this the refresh can be answered with exactly the stale markup it was
    // sent to replace. Measured: at 390 the drawer stayed empty against a cart
    // of two until this was added.
    const stamp = String(Date.now());
    const entries = await Promise.all(
      ids.map(async (id) => {
        const url = new URL(window.location.href);
        url.searchParams.set('mab_cart_refresh', stamp);
        return [id, await sectionRenderer.renderSection(id, { cache: false, url })];
      })
    );
    const sections = Object.fromEntries(entries.filter(([, html]) => typeof html === 'string'));

    let itemCount = countFromSections(sections);
    if (itemCount === null) itemCount = await countFromCart();
    if (itemCount === null) return;

    // The sections ride along so every other listener renders from the same
    // answer rather than fetching its own, and the header count comes from it
    // too, which is what keeps the two from ever disagreeing.
    document.dispatchEvent(
      new CartUpdateEvent({}, 'mab-cart-refresh', {
        source: 'mab-cart-refresh',
        itemCount,
        sections,
      })
    );
  } catch (error) {
    // A refresh that fails leaves the page showing what it was already showing.
    // Never throw out of a pageshow handler.
    console.warn(`[mab-cart-refresh] ${reason}:`, error);
  } finally {
    lastRun = Date.now();
    inFlight = false;
  }
}

/**
 * Two tabs on one cart, told directly rather than guessed at.
 *
 * Focus and visibility are not dependable for this. A second window side by
 * side with the first never hides it, and a browser is free to keep a
 * background tab marked visible. So the tab that changes the cart says so out
 * loud, and the others act on it whether or not anyone is looking at them.
 * Every mutation in this theme raises `cart:update`, adds included, so one
 * listener covers all of them.
 */
const channel = 'BroadcastChannel' in window ? new BroadcastChannel('mab-cart') : null;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  // Not our own re-render, or the tabs would talk each other in circles.
  if (event?.detail?.data?.source === 'mab-cart-refresh') return;
  channel?.postMessage({ at: Date.now() });
});

channel?.addEventListener('message', () => {
  refreshCartFromServer('another tab changed the cart');
});

/**
 * The other way Back comes back: a fresh document served out of the HTTP cache.
 *
 * Pressing Back does not always restore from the back-forward cache. The
 * browser may instead load the page again and answer that load from its own
 * HTTP cache, which hands back the HTML as it was when that URL was last
 * fetched. `pageshow` then fires with `persisted` false, because as far as the
 * document is concerned this is an ordinary load, and the markup is a
 * server-rendered picture of a cart from some earlier moment.
 *
 * Measured on this very fix: a Back that reported `persisted` false showed an
 * empty drawer while the cart held two items, because that URL had last been
 * fetched when the cart was empty. Which of the two happens is not something a
 * page gets to decide, so both are covered.
 *
 * Only history navigations, so this costs nothing on an ordinary page view.
 */
if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
  refreshCartFromServer('a fresh load served from the HTTP cache on Back');
}

/** The restore itself. `persisted` is the browser saying it kept the document. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartFromServer('restored from the back-forward cache');
});

/**
 * The other half of the same problem, with no navigation in it: two tabs on one
 * cart. The idle one holds a picture from whenever it was last touched, and the
 * buyer adding something in the other tab has no way to tell it. Coming back to
 * a tab is the moment to look again.
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS) {
    refreshCartFromServer('returned to a tab that had been left');
  }
  hiddenAt = 0;
});

/**
 * And the same again on focus, because visibility is not the whole story. A
 * second window side by side with the first is visible the entire time it is
 * ignored, and some browsers keep a backgrounded tab marked visible as well.
 * Losing and regaining focus is the signal that survives both.
 */
window.addEventListener('blur', () => {
  if (!blurredAt) blurredAt = Date.now();
});

window.addEventListener('focus', () => {
  if (blurredAt && Date.now() - blurredAt >= AWAY_MS) {
    refreshCartFromServer('focus came back to a window that had been left');
  }
  blurredAt = 0;
});
